import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import libphonenumber from 'google-libphonenumber'
import { EnqueueSnackbar } from 'notistack'

import Api from './api'
import { LOCAL_STORAGE_KEYS } from './constants'

/**
 *
 * @param address Can be a string or an object with address, locality, region, country, code
 * @param country If `undefined`, country is stripped from the address
 * @returns
 */
export const formatAddress = (address: IFolderType['information']['address'] | string, country = '') => {
  let formatted =
    typeof address === 'string'
      ? address
      : [address.address, address.address2, address.locality, `${address.region} ${address.code}`.trim(), address.country].filter((x) => x).join(', ')

  if (country === 'United States' || country === 'USA' || country === 'US') {
    formatted = formatted
      .replace(' United States ', ' ')
      .replace(' United States', '')
      .replace(' USA ', ' ')
      .replace(' USA', '')
      .replace(' US ', ' ')
      .replace(' US', '')
  }

  // reduce last line to nothing if locality, region, country, code does not exist
  formatted = formatted.replace(/\n(\s*,\s*)/, '\n')
  // reduce multiple new lines to single new line
  formatted = formatted.replace(/\n\n/g, '\n')
  // reduce all multiple spaces to single space
  formatted = formatted.replace(/[ ][ ]+/g, ' ')
  // remove floating comma at the beginning
  formatted = formatted.replace(/^,[ ]+/g, '')
  // remove all extra white space at the beginning/end
  formatted = formatted.trim()
  // trim extra comma at the end if locality exists but not region, country, code
  formatted = formatted.replace(/,$/, '')

  return formatted
}

/** Get list of parent folders in hierarchical order */
export const getFolderParents = (currentFolder: number, folderTree: IFolderTree) => {
  const parentFolders: number[] = []

  let nextParentFolder = null
  let currentIterationFolder = currentFolder

  while (folderTree[currentIterationFolder] && currentIterationFolder !== folderTree[currentIterationFolder].parentId) {
    nextParentFolder = folderTree[currentIterationFolder].parentId

    if (nextParentFolder !== undefined) {
      parentFolders.unshift(nextParentFolder)
      currentIterationFolder = nextParentFolder
      nextParentFolder = null
    } else {
      break
    }
  }
  return parentFolders
}

interface IGetFolderBreadcrumbs {
  tree: IFolderTree
  folderId?: number
  hierarchy?: number[]
  divider?: string
}
type GetFolderBreadcrumbsParams = RequireOnlyOne<IGetFolderBreadcrumbs, 'folderId' | 'hierarchy'>
/** Get the folder parent breadcrumbs (up to three levels up)
 * @param tree The folder tree
 * @param folderId The folder id if not passing in the folder hierarchy
 * @param hierarchy The folder hierarchy if not passing in the folder id
 * @param divider The divider to use between breadcrumbs (default is ' • ')
 */
export const getFolderBreadcrumbs = ({ tree, folderId, hierarchy, divider = ' • ' }: GetFolderBreadcrumbsParams) => {
  let folderHierarchy = hierarchy ?? []
  if (!hierarchy && folderId) folderHierarchy = getFolderParents(folderId, tree)

  const breadcrumbs = []
  if (folderHierarchy.length > 0 && tree[folderHierarchy[folderHierarchy.length - 1]]) breadcrumbs.push(tree[folderHierarchy[folderHierarchy.length - 1]].name)
  if (folderHierarchy.length > 1 && tree[folderHierarchy[folderHierarchy.length - 2]]) breadcrumbs.push(tree[folderHierarchy[folderHierarchy.length - 2]].name)
  if (folderHierarchy.length > 2 && tree[folderHierarchy[folderHierarchy.length - 3]]) breadcrumbs.push(tree[folderHierarchy[folderHierarchy.length - 3]].name)
  return breadcrumbs.join(divider)
}

export const isValidTimezone = (timezone: string) => {
  let isValid = false

  try {
    Intl.DateTimeFormat('en-US', { timeZone: timezone })
    isValid = true
  } catch {
    isValid = false
  }

  return isValid
}

export const getFormattedDate = (date: string, dateFormat?: string) => {
  const parsedDate = new Date(date)

  // May is month 4 (zero-based index)
  // and for May we don't want a dot
  const defaultFormat = parsedDate.getMonth() === 4 ? 'LLL d, yyyy' : 'LLL. d, yyyy'

  return format(new Date(date), dateFormat || defaultFormat)
}

export const getFormattedTime = (date: string, dateFormat?: string) => format(new Date(date), dateFormat || "h:mm aa 'UTC'")

export const getFormattedDateTime = (date: string, timezone?: string, dateFormat?: string) =>
  formatInTimeZone(new Date(date), timezone || 'UTC', dateFormat || "MM/dd/yyyy, h:mm aa 'UTC'")

export const getLocalFormattedDate = (timezone: string, dateFormat: string) => formatInTimeZone(new Date(), timezone, dateFormat)

const PNF = libphonenumber.PhoneNumberFormat
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()
// Use this method when displaying a phone number
// The phone number should include the country code; if it doesn't, we'll try to assume the country code is +1 (US)
// If the number is still not valid, return the same number back
export const prettifyPhoneNumber = (phoneNumber: string) => {
  try {
    const number = phoneUtil.parse(phoneNumber)
    const countryCode = number.getCountryCode()
    // Format US numbers as (xxx) xxx-xxxx
    if (countryCode === 1) {
      return phoneUtil.format(number, PNF.NATIONAL)
    }
    // Otherwise format as an international number as +xx xxx xxx xxxx
    return phoneUtil.format(number, PNF.INTERNATIONAL)
  } catch (e) {
    // If there is no country code, assume it's US and format as (xxx) xxx-xxxx
    if ((e as { message: string }).message.includes('Invalid country calling code')) {
      try {
        return phoneUtil.format(phoneUtil.parse(`+1 ${phoneNumber}`), PNF.NATIONAL)
      } catch {
        //
      }
    }
    return phoneNumber
  }
}

export const convertStringToArray = (str: string) => {
  if (str?.trim().length === 0) return []

  let removedBrackets = str
  if (removedBrackets.charAt(0) === '[') removedBrackets = removedBrackets.slice(1)
  if (removedBrackets.charAt(removedBrackets.length - 1) === ']') removedBrackets = removedBrackets.slice(0, -1)
  return removedBrackets.split(',').map((item) => {
    let newItem = item.trim()
    if (['"', "'"].includes(newItem.charAt(0))) newItem = newItem.slice(1)
    if (['"', "'"].includes(newItem.charAt(newItem.length - 1))) newItem = newItem.slice(0, -1)
    return newItem.trim()
  })
}

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export const getLogo = (accountTheme: IFolderTheme | undefined, host: string) =>
  accountTheme?.companyLogo
    ? `${host}/api/files/public/${accountTheme.companyLogo}` // First choice: account theme companyLogo
    : (accountTheme?.logoUrl as string) // Second choice: account theme logoUrl (full path)

export const openSensorStore = async (folderId: number, enqueueSnackbar: EnqueueSnackbar, accessToken: string, host: string) => {
  try {
    const storeLink = await Api.get(`${host}/api/v2/protect/folders/${folderId}/shopify-link`)
    if (storeLink.ok && storeLink?.body?.url) {
      const { url } = storeLink.body as { url: string }
      window.open(url, '_blank')?.focus()
    } else {
      enqueueSnackbar('Sensor Store URL could not be found for this folder')
    }
  } catch {
    enqueueSnackbar('Sensor Store URL could not be found for this folder')
  }
}

export const getVanityName = (vanityName: string) => {
  const result = vanityName.startsWith('Meshify ') || vanityName.startsWith('meshify ') ? vanityName.slice(8) : vanityName
  return result
}

export const getFirmwareVersion = (firmwareVersion: string) =>
  firmwareVersion && firmwareVersion !== '<nil>' && firmwareVersion !== '0' ? firmwareVersion : ''

export const showDeviceMenuItems = (device: IDeviceAPIObject) => {
  const showActivate = device.deviceStatus === 'Not Activated' || device.deviceStatus === 'Extra'
  const showEditDetails =
    device.deviceStatus === 'Paused' || device.deviceStatus === 'Online' || device.deviceStatus === 'Offline' || device.deviceStatus === 'Extra'

  const showPause = device.deviceStatus === 'Online' || device.deviceStatus === 'Offline'
  const showUnpause = device.deviceStatus === 'Paused'

  const showRMA =
    device.deviceStatus === 'Paused' ||
    device.deviceStatus === 'Online' ||
    device.deviceStatus === 'Offline' ||
    device.deviceStatus === 'Extra' ||
    device.deviceStatus === 'Not Activated'

  const showDeactivate =
    device.deviceStatus === 'Paused' ||
    device.deviceStatus === 'Online' ||
    device.deviceStatus === 'Offline' ||
    device.deviceStatus === 'Extra' ||
    device.deviceStatus === 'Not Activated' ||
    device.deviceStatus === 'Audible Only'

  const showValve = (device.nodeType.name === 'bvs' || device.nodeType.name === 'bvs2') && device.deviceStatus !== 'Deactivated'

  const showReactivate = device.deviceStatus === 'Deactivated'

  let resourcesLink = ''
  if (device.nodeType.name === 'bvs2') resourcesLink = 'https://meshify.com/resources/shutoff/'
  if (device.nodeType.name === 'leak3') resourcesLink = 'https://meshify.com/resources/defender-sensors/'
  if (device.nodeType.name === 'temp3') resourcesLink = 'https://meshify.com/resources/defender-sensors/'
  if (device.nodeType.name === 'nbiot_leak') resourcesLink = 'https://meshify.com/resources/leakfreeze/'
  if (device.nodeType.name === 'gateway') resourcesLink = 'https://meshify.com/resources/gateway/'

  return { showActivate, showEditDetails, showPause, showUnpause, showRMA, showDeactivate, showValve, resourcesLink, showReactivate }
}

export const isGateway = (nodeType?: IDeviceAPIObject['nodeType'] | string) => {
  if (typeof nodeType === 'string') return nodeType === 'gateway' || nodeType === 'kerlink_wifi_gateway'
  if ((nodeType as IDeviceAPIObject['nodeType'])?.properties) return !!(nodeType as IDeviceAPIObject['nodeType']).properties.includes('gateway')
  if ((nodeType as IDeviceAPIObject['nodeType'])?.name)
    return (nodeType as IDeviceAPIObject['nodeType']).name === 'gateway' || (nodeType as IDeviceAPIObject['nodeType']).name === 'kerlink_wifi_gateway'
  return false
}

export const locationsListArray = (folderTheme: IFolderTheme | undefined) => {
  let locationsList: string[] = []
  if (folderTheme?.deviceLocations) {
    locationsList = typeof folderTheme.deviceLocations === 'string' ? convertStringToArray(folderTheme.deviceLocations) : folderTheme.deviceLocations
  }
  return locationsList
}

const RECENTLY_LOGOUT_THRESHOLD_MS = 30000 // 30 seconds
/**
 * Check if logout happened recently (within 30 seconds)
 */
export const checkIsRecentlyLoggedOut = () => {
  const loggedOutTimestamp = localStorage.getItem(LOCAL_STORAGE_KEYS.LOGGED_OUT)
  return !!(loggedOutTimestamp && Date.now() - parseInt(loggedOutTimestamp, 10) < RECENTLY_LOGOUT_THRESHOLD_MS)
}

export const reactivateDevice = async (
  deviceId: number | string,
  host: string | undefined,
  accessToken: string | undefined,
  enqueueSnackbar: EnqueueSnackbar,
  onSuccess?: () => void | Promise<void>
) => {
  if (!host || !accessToken) return

  // PUT is acting like a PATCH here, only updating the isActive field to true to reactivate the device
  // and keeps all other fields not changed
  const res = await Api.put(`${host}/api/nodes/${deviceId}`, { isActive: true })

  if (res.ok) {
    if (onSuccess) {
      await onSuccess()
    }
    enqueueSnackbar('Device reactivated successfully')
  } else {
    enqueueSnackbar('Could not reactivate', { variant: 'error' })
  }
}
